import { normIcao } from "./airportFrequencyLocal";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

export function effectiveTachHours(leg: FlightLeg): number | null {
  const a = leg.tachStart ?? null;
  const b = leg.tachEnd ?? null;
  if (a === null || b === null) return null;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const d = b - a;
  return d >= 0 ? d : null;
}

/** Hobbs/tach column, or tach end − start when hours blank. */
export function effectiveFlightHours(leg: FlightLeg): number | null {
  const h = leg.flightHours;
  if (h !== null && h !== undefined && Number.isFinite(h) && h >= 0) return h;
  return effectiveTachHours(leg);
}

export function resolvedFuelGallons(leg: FlightLeg, lbsPerGallon: number): number | null {
  if (leg.fuelGallonsReported !== null && leg.fuelGallonsReported !== undefined) {
    return leg.fuelGallonsReported;
  }
  if (leg.fuelLbs !== null && leg.fuelLbs !== undefined && lbsPerGallon > 0) {
    return leg.fuelLbs / lbsPerGallon;
  }
  return null;
}

export function fuelCostUsd(leg: FlightLeg, rates: Rates): number | null {
  const gal = resolvedFuelGallons(leg, rates.fuelLbsPerGallon);
  const ppg = leg.fuelPricePerGallon;
  if (gal === null || ppg === null || ppg === undefined || !Number.isFinite(ppg) || ppg < 0) {
    return null;
  }
  return money(gal * ppg);
}

/** Personal legs: 2× fuel cost owed to Utah Gas Corp (contract fuel rule). */
export function personalFuelOwed2x(
  leg: FlightLeg,
  rates: Rates,
  isPersonal: boolean = leg.personalUse
): number {
  if (!isPersonal) return 0;
  const c = fuelCostUsd(leg, rates);
  return c === null ? 0 : money(c * 2);
}

function effectiveLandingsCount(leg: FlightLeg): number {
  const n = leg.landings;
  if (n != null && Number.isFinite(n) && n >= 0) return Math.max(0, Math.round(n));
  return 1;
}

/** Takeoffs on this leg; blank → 1 (matches Honda / company rollups). */
export function effectiveTakeoffsCount(leg: FlightLeg): number {
  const n = leg.takeoffs;
  if (n != null && Number.isFinite(n) && n >= 0) return Math.max(0, Math.round(n));
  return 1;
}

export type TexasLandingOptions = {
  /** When set, also count landings whose destination ICAO is in this set (TX airports). */
  inferDestinationTexasIcao?: ReadonlySet<string>;
  /**
   * When <code>departure_state</code> is blank, count takeoffs as Texas departures if <code>from</code>
   * ICAO is in this set (same TX list as destination infer; optional in callers).
   */
  inferOriginTexasIcao?: ReadonlySet<string>;
};

/** Texas landings: log flag and/or inferred from destination airport (see texasIcao). */
export function effectiveTexasLandings(
  leg: FlightLeg,
  opts?: TexasLandingOptions
): number {
  const n = effectiveLandingsCount(leg);
  if (leg.texasArrival) return n;
  const set = opts?.inferDestinationTexasIcao;
  if (set && set.has(normIcao(leg.toIcao))) return n;
  return 0;
}

/**
 * Departure from Texas for Section 21.055-style counts: <code>departure_state</code> TX and/or
 * <code>from</code> ICAO in the infer set (same rules as takeoffsTexas in {@link summarizeCompanyHonda}).
 */
export function isTexasDepartureLeg(leg: FlightLeg, opts?: TexasLandingOptions): boolean {
  const s = (leg.departureState ?? "").trim().toUpperCase();
  if (s === "TX") return true;
  if (s.length === 2 && s !== "TX") return false;
  const originSet = opts?.inferOriginTexasIcao;
  if (originSet && originSet.has(normIcao(leg.fromIcao))) return true;
  return false;
}

/** Sum of leg takeoffs that depart Texas (blank takeoffs → 1). */
export function effectiveTexasDepartures(
  leg: FlightLeg,
  opts?: TexasLandingOptions
): number {
  if (!isTexasDepartureLeg(leg, opts)) return 0;
  return effectiveTakeoffsCount(leg);
}
